import React from 'react';
import { Panel, Label } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import PagedEntitiesList from '../../common/components/PagedEntitiesList';
import LabelWithTooltip from '../../common/components/LabelWithTooltip';
import ui from '../../common/services/ui';
import { withTranslation } from 'react-i18next';
import { PaymentHistoryMode, mapStatusToLabel } from './utils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHandshake } from '@fortawesome/free-regular-svg-icons';
import { faAlignJustify, faExchange, faTriangleExclamation, faLink } from '@fortawesome/free-solid-svg-icons';

const renderPaymentMethodCell = (paymentMethod) => {
	return paymentMethod ? paymentMethod.name : '';
};

const renderTotalCell = (remainingTotal, row) => {
	return ui.formatAmount(remainingTotal) + (row.refundedTotal ? ` (${ui.formatAmount(row.refundedTotal)
		})` : '');
};

const renderQuantityCell = (remainingQuantity, row) => {
	return remainingQuantity + (row.refundedQuantity ? ` (${row.refundedQuantity})` : '');
};

const List = ({ mode, isReadonly, t, parentId, canSendMessage, listActions, showDiscountNameOnLessonPayments, ...rest }) => {
	const columns = [
		{
			header: t('PaymentHistory_Id'),
			accessor: 'id',
			sortable: true,
			cell: (id, row) => (<Link to={`/paymentHistory/${id}`} >
				{id}&nbsp;
				{row.isTransferred && <LabelWithTooltip id='transferTooltip' tooltip={t('PaymentHistory_TransferTooltip')}><FontAwesomeIcon icon={faExchange} /></LabelWithTooltip>}
				{row.paymentParentId && <LabelWithTooltip id='parentTooltip' tooltip={t('PaymentHistory_ParentTooltip')}><FontAwesomeIcon icon={faLink} /></LabelWithTooltip>}
				{row.paymentType && row.paymentType.id == 7 && <LabelWithTooltip id='annualTooltip' tooltip={t('PaymentHistory_AnnualTooltip')}><FontAwesomeIcon icon={faAlignJustify} /></LabelWithTooltip>}
				{row.isPaymentError && !row.isInactive && <LabelWithTooltip id='еrrorTooltip' tooltip={t('PaymentHistory_ErrorTooltip')}><FontAwesomeIcon style={{ color: 'yellow' }} icon={faTriangleExclamation} /></LabelWithTooltip>}
			</Link>)
		}
	];
	if (!parentId || parentId === 0) {
		columns.push(
			{
				header: t('PaymentHistory_Parent'),
				accessor: 'parent.fullname',
				cell: (parent, row) => {
					if (parentId > 0) {
						return row.parent.fullname;
					}
					else {
						return <Link to={`/parents/${row.parent.id}`} > {row.parent.fullname}</Link>;
					}
				},
				sortable: true
			});
	}
	columns.push({
		header: t('PaymentHistory_Child'),
		accessor: 'child.fullname',
		cell: (child, row) => {
			if (parentId > 0) {
				return row.child.fullname;
			}
			else {
				return <Link to={`/parents/${row.parent.id}/children/${row.child.id}`} >
					{row.child.fullname} {row.childHasEnrolmentInTerm ? <LabelWithTooltip id='hasLessonsTooltip' tooltip={t('PaymentHistory_HasLessonsTooltip')}><FontAwesomeIcon icon={faHandshake} style={{ color: 'black' }} /></LabelWithTooltip> : ''}
				</Link>
			}
		},
		sortable: true
	});
	columns.push({
		header: t('PaymentHistory_Center'),
		accessor: 'center',
		sortable: true
	});
	columns.push({
		header: t('PaymentHistory_NumberOfLessons'),
		accessor: 'remainingQuantity',
		sortable: true,
		cell: renderQuantityCell
	});
	columns.push({
		header: t('PaymentHistory_Total'),
		accessor: 'remainingTotal',
		sortable: true,
		cell: renderTotalCell
	});
	columns.push({
		header: t('PaymentHistory_PaidTotal'),
		accessor: 'paidTotal',
		sortable: false
	});
	columns.push({
		header: t('PaymentHistory_OutstandingTotal'),
		accessor: 'outstandingTotal',
		sortable: false
	});
	columns.push({
		header: t('PaymentHistory_DiscountAmount'),
		accessor: 'discount',
		sortable: true
	});
	if (showDiscountNameOnLessonPayments) {
		columns.push({
			header: t('PaymentHistory_DiscountName'),
			accessor: 'discountName'
		});
	}
	columns.push({
		header: t('PaymentHistory_Status'),
		accessor: 'status',
		sortable: true,
		cell: (status, row) => <span> <Label bsStyle={mapStatusToLabel(status.id)}>{t(`PaymentStatusType_${status.name}`)}</Label> {row.isInactive && <Label bsStyle='inverse'>{t('PaymentStatus_Inactive')}</Label>}</span>
	});
	columns.push({
		header: t('ReportChildren_Grade'),
		accessor: 'grade',
		cell: (grade, row) => row.name || grade,
		sortable: true
	});
	columns.push({
		header: t('PaymentHistory_PaymentType'),
		accessor: 'paymentType',
		cell: (paymentType) => <span>{t(`PaymentTypeEnum_${paymentType.name}`)}</span>,

		sortable: true
	});
	columns.push({
		header: t('ReportChildren_WeekDay'),
		accessor: 'dayOfWeek',
		cell: dayOfWeek => dayOfWeek && t(ui.days[dayOfWeek]),
		sortable: true
	});
	columns.push({
		header: t('PaymentHistory_Created'),
		accessor: 'created',
		sortable: true,
		cell: (created) => (ui.formatDateTime(created))
	});
	columns.push({
		header: t('PaymentHistory_StartDate'),
		accessor: 'startDate',
		sortable: true,
		cell: (startDate) => (ui.formatDate(startDate))
	});
	columns.push({
		header: t('PaymentHistory_StartTime'),
		accessor: 'startTime',
		sortable: true,
		cell: (startTime) => (ui.formatTime(startTime))
	});
	if (mode === PaymentHistoryMode.ALL) {
		columns.push({
			header: t('PaymentHistory_PaymentDate'),
			accessor: 'paymentDate',
			sortable: true,
			cell: (paymentDate) => (ui.formatDateTime(paymentDate))
		});
	}
	columns.push({
		header: t('PaymentHistory_PaymentMethod'),
		accessor: 'paymentMethod',
		sortable: true,
		cell: (paymentMethod) => renderPaymentMethodCell(paymentMethod)
	});
	columns.push({
		header: t('Common_Actions'),
		accessor: 'id',
		cell: (id, row) => (<div>
			<Link to={`/paymentHistory/${id}`} className='btn btn-teal'> {t('Common_View')}</Link>
		</div>)
	});

	return (
		<Panel>
			<Panel.Body>
				<PagedEntitiesList columns={columns} checkboxSelection={!parentId && canSendMessage} listActions={listActions} {...rest} />
			</Panel.Body>
		</Panel>
	);
};

export default withTranslation()(List);